/**
 * Utilitários para manipulação segura de datas
 * Resolve problemas de timezone e offset de datas
 */

/**
 * Converte uma data para string no formato YYYY-MM-DD sem conversão UTC
 * @param date - Data a ser formatada
 * @returns String no formato YYYY-MM-DD
 */
export const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Converte uma string de data YYYY-MM-DD para Date forçando interpretação local.
 * Evita o problema de timezone UTC que causa offset de -1 dia.
 *
 * Hardening (2026-05-20): retorna `null` em vez de throw quando recebe valor
 * inválido (string vazia, undefined, formato malformado). Antes, throw aqui
 * propagava silenciosamente por callers em try/catch e em alguns casos
 * corrompia state — combinado com a ausência de ErrorBoundary, podia gerar
 * tela em branco quando paciente importado vinha com `birthDate: ''`.
 *
 * @param dateString - String no formato YYYY-MM-DD, ou Date object
 * @returns Date object com timezone local, ou null se input inválido
 */
export const parseLocalDate = (dateString: string | Date | null | undefined): Date | null => {
  // Se já é um objeto Date, retorna como está
  if (dateString instanceof Date) {
    return isNaN(dateString.getTime()) ? null : dateString;
  }

  // Inputs vazios/undefined = null (não é erro semântico, só ausência de dado)
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }

  // Parse manual para evitar interpretação UTC
  const parts = dateString.split('-');
  if (parts.length !== 3) {
    return null;
  }

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Mês é 0-indexado
  const day = parseInt(parts[2], 10);

  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    return null;
  }

  // Criar data com timezone local
  const result = new Date(year, month, day);
  return isNaN(result.getTime()) ? null : result;
};

/**
 * Converte valor de input type="date" para string YYYY-MM-DD segura
 * Evita problemas de timezone que causam offset de -1 dia
 * @param inputValue - Valor do input type="date"
 * @returns String no formato YYYY-MM-DD sem conversão UTC
 */
export const formatInputDateValue = (inputValue: string): string => {
  if (!inputValue) return '';
  
  // Se já está no formato correto, retorna como está
  if (/^\d{4}-\d{2}-\d{2}$/.test(inputValue)) {
    return inputValue;
  }
  
  // Se é uma data ISO, extrair apenas a parte da data
  if (inputValue.includes('T')) {
    return inputValue.split('T')[0];
  }
  
  // Tentar converter para Date e formatar localmente
  const date = parseLocalDate(inputValue);
  return date ? formatLocalDate(date) : inputValue;
};

/**
 * Converte string YYYY-MM-DD para valor seguro para input type="date"
 * Garante que não há conversão UTC que cause offset
 * @param dateString - String de data
 * @returns String formatada para input type="date"
 */
export const toInputDateValue = (dateString: string | Date | null | undefined): string => {
  if (!dateString) return '';

  if (dateString instanceof Date) {
    return isNaN(dateString.getTime()) ? '' : formatLocalDate(dateString);
  }

  // Se é ISO string, extrair apenas a parte da data
  if (typeof dateString === 'string' && dateString.includes('T')) {
    return dateString.split('T')[0];
  }

  // Se já está no formato YYYY-MM-DD, retorna como está
  if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  // Tentar converter
  const date = parseLocalDate(dateString);
  return date ? formatLocalDate(date) : '';
};

/**
 * Converte uma data (string ou Date) para Date object de forma segura
 * @param date - Data como string YYYY-MM-DD, ISO string, ou Date object
 * @returns Date object com timezone local
 */
export const ensureLocalDate = (date: string | Date | null | undefined): Date => {
  if (date instanceof Date && !isNaN(date.getTime())) {
    return date;
  }

  // Se é uma string ISO (contém T), extrair apenas a parte da data para evitar conversão de timezone
  let dateOnly: string | Date | null | undefined = date;
  if (typeof date === 'string' && date.includes('T')) {
    dateOnly = date.split('T')[0];
  }

  // parseLocalDate agora retorna null em vez de throw. Mantemos compatibilidade
  // legacy retornando epoch (1970-01-01) como fallback seguro — alternativa
  // seria mudar assinatura pra Date | null e quebrar 20+ callers.
  return parseLocalDate(dateOnly) ?? new Date(0);
};

/**
 * Verifica se duas datas representam o mesmo dia (ignora horário)
 * @param date1 - Primeira data
 * @param date2 - Segunda data
 * @returns true se são o mesmo dia
 */
export const isSameDay = (date1: string | Date, date2: string | Date): boolean => {
  const d1 = ensureLocalDate(date1);
  const d2 = ensureLocalDate(date2);
  
  return (
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear()
  );
};

/**
 * Verifica se uma data está dentro de uma semana específica
 * @param date - Data a verificar
 * @param weekStartDate - Data de início da semana
 * @returns true se a data está na semana
 */
export const isDateInWeek = (date: string | Date, weekStartDate: Date): boolean => {
  const targetDate = ensureLocalDate(date);
  
  const startOfWeek = new Date(weekStartDate);
  startOfWeek.setDate(weekStartDate.getDate() - weekStartDate.getDay() + 1);
  
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  
  return targetDate >= startOfWeek && targetDate <= endOfWeek;
};

/**
 * Verifica se uma data está no mesmo mês e ano
 * @param date - Data a verificar
 * @param monthDate - Data de referência do mês
 * @returns true se está no mesmo mês
 */
export const isDateInMonth = (date: string | Date, monthDate: Date): boolean => {
  const targetDate = ensureLocalDate(date);
  
  return (
    targetDate.getMonth() === monthDate.getMonth() &&
    targetDate.getFullYear() === monthDate.getFullYear()
  );
};